import className from 'classnames/bind';
import { useMemo, useRef } from 'react';
import { Container, RevealWrapper } from '@components';
import { GetInducteesByClassQuery } from '@graphqlTypes';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import styles from './ListingInductees.module.scss';
import { getHighlightMarkup, getInducteeCategoryLabel, groupInducteesByType, replaceStringVar, sortInductees } from '@/core/utils';
import ListingInducteeCard from './Card';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
const cx = className.bind(styles); 

interface ListingInducteesProps {
  inductees: GetInducteesByClassQuery["relatedInductees"];
  classYear: string;
  showHeader: boolean;
}

export type ListingInductees = GetInducteesByClassQuery['relatedInductees']['nodes'];

const groupOrder = ['performer', 'musical-influence', 'musical-excellence', 'ahmet-ertegun-award'];

const InducteeGroup = ({ group, inductees }: { group: string; inductees: ListingInductees; }) => {
  const { labels } = usePageWrapperData();
  const groupCopy = getInducteeCategoryLabel(group, labels);
  const showHeader = Boolean(groupCopy);
  const sortedInductees = useMemo(() => sortInductees(inductees), [inductees]);

  return (
    <div key={group} className={cx("group")}>
      {
        showHeader && (
          <div className={cx("group__copy")}>
            <h2 className={cx("title", "title-uc-s")}>{groupCopy?.headline}</h2>
            <p className={cx("body", "text-m")}>{groupCopy?.body}</p>
          </div>
        )
      }
      <div className={cx("group__list")}>
        {sortedInductees?.map((inductee) => (
          <ListingInducteeCard key={inductee.uri} {...inductee} />
        ))}
      </div>
    </div>
  );
}

export default function ListingInductees({ inductees, classYear, showHeader }: ListingInducteesProps) {
  const { labels } = usePageWrapperData();
  const { headline, body } = labels?.listingInducteesCopy || {};
  const groupedInductees = groupInducteesByType(inductees.nodes);
  const headlineMarkup = getHighlightMarkup(replaceStringVar(headline, { year: classYear }));
  const bodyCopy = replaceStringVar(body, { year: classYear });

  const { isDesktop } = useViewSize();
  const rootEl = useRef<HTMLDivElement>(null);
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  return (
    <section className={cx(["listing-inductees"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container>
          {showHeader && (
            <div className={cx(["listing-inductees__header"])}>
              <h2 className={cx("title", "title-uc-xl")} dangerouslySetInnerHTML={{ __html: headlineMarkup }} />
              <p className={cx("body", "text-l")}>{bodyCopy}</p>
            </div>
          )}
          <div className={cx(["listing-inductees__groups"])}>
            {groupOrder.map((group) => (
              groupedInductees[group] ? (
                <InducteeGroup key={group} group={group} inductees={groupedInductees[group]} />
              ) : null
            ))}
          </div>
        </Container>
      </RevealWrapper>
    </section>
  )
}